import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { AIText } from "@/components/common/AIText";
import { Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

/** Contextual assistant: invoked on demand. Keeps a short thread for the current session only (never persisted). */
export function AssistantPanel({ open, onOpenChange, contextType, contextId, title, description, suggestions = [] }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]);
  const endRef = useRef(null);

  useEffect(() => { setThread([]); setQ(""); }, [contextType, contextId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread.length, busy]);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (text.length < 3) return;
    setBusy(true); setQ("");
    setThread((t) => [...t, { role: "user", text }]);
    try {
      const { data } = await api.post("/ai/assistant", { question: text, context_type: contextType, context_id: contextId || null });
      setThread((t) => [...t, { role: "assistant", ...data }]);
    } catch (e) { toast.error(errMsg(e)); setThread((t) => t.slice(0, -1)); setQ(text); } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[560px] p-0 flex flex-col" data-testid="assistant-panel">
        <SheetHeader className="p-5 pb-3 border-b border-border shrink-0">
          <SheetTitle className="font-heading flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> {title || "Ask about this"}</SheetTitle>
          <SheetDescription>{description || "Answers use only the data in this context. Evidence gaps are stated explicitly."}</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto min-h-0 p-5 space-y-3">
          {thread.length === 0 && suggestions.length > 0 && (
            <div className="space-y-2">
              <div className="meta">Try one of these</div>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((s) => <button key={s} onClick={() => ask(s)} className="chip chip-muted hover:bg-accent text-left whitespace-normal" data-testid="assistant-suggestion">{s}</button>)}
              </div>
            </div>
          )}
          {thread.map((m, i) => m.role === "user" ? (
            <div key={i} className="flex justify-end"><div className="max-w-[85%] rounded-lg bg-accent text-accent-foreground px-3 py-2 text-sm" data-testid="assistant-user-message">{m.text}</div></div>
          ) : (
            <div key={i} className="panel p-3 fade-up" data-testid="assistant-answer">
              <AIText text={m.answer} />
              <div className="meta mt-2">{m.provider}/{m.model} · {m.funding_source} · ${m.cost_usd?.toFixed(4)}</div>
            </div>
          ))}
          {busy && <div className="panel p-3 text-sm text-muted-foreground animate-pulse" data-testid="assistant-thinking">Reading the data…</div>}
          <div ref={endRef} />
        </div>
        <div className="p-4 border-t border-border shrink-0 space-y-2 bg-card">
          <Textarea value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ask(); }} placeholder="Ask a question about this data… (Ctrl+Enter to send)" rows={2} data-testid="assistant-question" />
          <div className="flex items-center justify-between">
            <Button onClick={() => ask()} disabled={busy || q.trim().length < 3} data-testid="assistant-ask-button">{busy ? "Thinking…" : "Ask"}</Button>
            {thread.length > 0 && <Button variant="ghost" size="sm" onClick={() => setThread([])} data-testid="assistant-clear"><Trash2 className="h-3.5 w-3.5 mr-1" />Clear</Button>}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
